import { useEffect } from "react";
import type { AuthenticationPlugin } from "zudoku";
import { useAuth } from "zudoku/hooks";
import { Key, LogOut } from "zudoku/icons";
import { getAuthState, type UserProfile } from "./authState";
import { OAuthCallback } from "./OAuthCallback";

export interface CustomAuthConfig {
	authApiUrl: string;
	redirectToAfterSignIn?: string;
	redirectToAfterSignOut?: string;
	enablePasswordAuth?: boolean; // Enable email+password flow (default: false, SSO only)
}

interface TokenData {
	access_token: string;
	refresh_token: string;
	organization_id: string | null;
	expires_at: number; // Unix timestamp
}

interface UserInfo {
	id: string;
	email: string;
	first_name: string;
	last_name: string;
	email_verified: boolean;
	profile_picture_url: string | null;
	created_at: string;
	updated_at: string;
}

const TOKEN_STORAGE_KEY = "beam_auth_tokens";
const USER_STORAGE_KEY = "beam_auth_user";
const CODE_VERIFIER_KEY = "beam_pkce_verifier";
const AUTH_STATE_KEY = "beam_auth_state";

export class CustomAuthProvider implements AuthenticationPlugin {
	private config: CustomAuthConfig;
	private tokenRefreshTimer?: number;
	private static instance: CustomAuthProvider | null = null;
	private initialized: boolean = false;

	constructor(config: CustomAuthConfig) {
		this.config = {
			enablePasswordAuth: false,
			redirectToAfterSignIn: "/",
			redirectToAfterSignOut: "/",
			...config,
		};

		// Store instance for use in callback component
		CustomAuthProvider.instance = this;
	}

	/**
	 * Initialize the authentication provider
	 * Called by Zudoku when the app loads
	 */
	async initialize(): Promise<void> {
		// Skip initialization on server-side
		if (typeof window === "undefined") {
			return;
		}

		// Prevent re-initialization to avoid loops
		if (this.initialized) {
			console.log("[Auth] Already initialized, skipping");
			return;
		}

		console.log("[Auth] Initializing authentication provider");
		this.initialized = true;

		// Check if we have existing tokens
		const tokens = this.getStoredTokens();

		if (tokens && !this.isTokenExpired(tokens)) {
			console.log("[Auth] Found valid existing session");

			// Restore user profile from localStorage
			const storedUser = localStorage.getItem(USER_STORAGE_KEY);
			if (storedUser) {
				try {
					const user = JSON.parse(storedUser);
					const profile: UserProfile = {
						sub: user.id,
						email: user.email,
						emailVerified: user.email_verified,
						name:
							[user.first_name, user.last_name].filter(Boolean).join(" ") ||
							undefined,
						pictureUrl: user.profile_picture_url || undefined,
					};

					console.log("[Auth] Restoring user session", { email: user.email });

					const authState = getAuthState();
					if (authState) {
						authState.setLoggedIn({
							profile,
							providerData: { tokens },
						});
					}

					// Schedule token refresh
					this.scheduleTokenRefresh();
				} catch (error) {
					console.error("[Auth] Failed to restore user session:", error);
					this.clearStoredTokens();
				}
			} else {
				console.warn("[Auth] Tokens found but no user data, clearing session");
				this.clearStoredTokens();
			}
		} else {
			console.log("[Auth] No valid session found");
			if (tokens) {
				console.log("[Auth] Expired tokens found, clearing");
				this.clearStoredTokens();
			}

			// Clear pending state even when not authenticated
			const currentState = localStorage.getItem("auth-state");
			if (currentState) {
				try {
					const parsed = JSON.parse(currentState);
					// Only update if the state is actually pending
					if (parsed.state?.isPending) {
						const updated = {
							...parsed,
							state: {
								...parsed.state,
								isPending: false,
								isAuthenticated: false,
							},
						};
						localStorage.setItem("auth-state", JSON.stringify(updated));
						console.log("[Auth] Cleared pending state (no session)");
					}
				} catch (error) {
					console.error("[Auth] Failed to update pending state:", error);
				}
			}
		}

		console.log("[Auth] Authentication provider initialized");
	}

	/**
	 * Get the current instance (for use in callback component)
	 */
	static getInstance(): CustomAuthProvider | null {
		return CustomAuthProvider.instance;
	}

	/**
	 * Check if user is authenticated (for debugging)
	 */
	isAuthenticated(): boolean {
		const tokens = this.getStoredTokens();
		const isAuth = !!tokens && !this.isTokenExpired(tokens);
		console.log("[Auth] Authentication status check", {
			hasTokens: !!tokens,
			isExpired: tokens ? this.isTokenExpired(tokens) : null,
			isAuthenticated: isAuth,
		});
		return isAuth;
	}

	/**
	 * Get stored token data from localStorage
	 */
	private getStoredTokens(): TokenData | null {
		if (typeof window === "undefined") return null;

		const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
		if (!stored) return null;

		try {
			return JSON.parse(stored);
		} catch {
			return null;
		}
	}

	/**
	 * Store token data in localStorage
	 */
	private setStoredTokens(tokens: TokenData): void {
		if (typeof window === "undefined") return;
		localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
	}

	/**
	 * Clear stored tokens
	 */
	private clearStoredTokens(): void {
		if (typeof window === "undefined") return;
		localStorage.removeItem(TOKEN_STORAGE_KEY);
		localStorage.removeItem(USER_STORAGE_KEY);
	}

	/**
	 * Store user info in localStorage
	 */
	private setStoredUser(user: UserInfo): void {
		if (typeof window === "undefined") return;
		localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
	}

	/**
	 * Check if access token is expired or about to expire (within 5 minutes)
	 */
	private isTokenExpired(tokens: TokenData): boolean {
		const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
		return tokens.expires_at < fiveMinutesFromNow;
	}

	/**
	 * Refresh the access token using refresh token
	 */
	private async refreshAccessToken(): Promise<void> {
		const tokens = this.getStoredTokens();
		if (!tokens?.refresh_token) {
			throw new Error("No refresh token available");
		}

		try {
			// Try SSO refresh first (most common case)
			const response = await fetch(`${this.config.authApiUrl}/sso/refresh`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					refresh_token: tokens.refresh_token,
				}),
			});

			if (!response.ok) {
				// If SSO refresh fails, try password refresh
				const passwordResponse = await fetch(
					`${this.config.authApiUrl}/password/refresh`,
					{
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							refresh_token: tokens.refresh_token,
						}),
					},
				);

				if (!passwordResponse.ok) {
					throw new Error("Token refresh failed");
				}

				const data = await passwordResponse.json();
				this.handleTokenResponse(data);
				return;
			}

			const data = await response.json();
			this.handleTokenResponse(data);
		} catch (error) {
			console.error("Token refresh failed:", error);
			this.clearStoredTokens();
			throw error;
		}
	}

	/**
	 * Handle token response and store tokens
	 */
	private handleTokenResponse(data: any): void {
		console.log("[Auth] Handling token response", {
			hasAccessToken: !!data.access_token,
			hasRefreshToken: !!data.refresh_token,
			hasUser: !!data.user,
			organizationId: data.organization_id,
		});

		const expiresIn = 3600; // Default to 1 hour if not provided
		const expiresAt = Date.now() + expiresIn * 1000;

		const tokenData: TokenData = {
			access_token: data.access_token,
			refresh_token: data.refresh_token,
			organization_id: data.organization_id || null,
			expires_at: expiresAt,
		};

		this.setStoredTokens(tokenData);
		console.log("[Auth] Tokens stored in localStorage", {
			expiresAt: new Date(expiresAt).toISOString(),
		});

		if (data.user) {
			this.setStoredUser(data.user);
			console.log("[Auth] User info stored", {
				email: data.user.email,
				id: data.user.id,
			});

			// Update Zudoku's authentication state
			const profile = {
				sub: data.user.id,
				email: data.user.email,
				emailVerified: data.user.email_verified,
				name:
					[data.user.first_name, data.user.last_name]
						.filter(Boolean)
						.join(" ") || undefined,
				pictureUrl: data.user.profile_picture_url || undefined,
			};

			console.log("[Auth] Updating Zudoku auth state", { profile });
			const authState = getAuthState();
			if (authState) {
				authState.setLoggedIn({
					profile,
					providerData: { tokens: tokenData },
				});
			}
		}

		// Schedule token refresh
		this.scheduleTokenRefresh();
	}

	/**
	 * Schedule automatic token refresh before expiration
	 */
	private scheduleTokenRefresh(): void {
		if (this.tokenRefreshTimer) {
			clearTimeout(this.tokenRefreshTimer);
		}

		const tokens = this.getStoredTokens();
		if (!tokens) return;

		// Refresh 5 minutes before expiration
		const timeUntilRefresh = Math.max(
			0,
			tokens.expires_at - Date.now() - 5 * 60 * 1000,
		);

		this.tokenRefreshTimer = window.setTimeout(() => {
			this.refreshAccessToken().catch((error) => {
				console.error("Automatic token refresh failed:", error);
			});
		}, timeUntilRefresh);
	}

	/**
	 * Generate a random string for PKCE code verifier
	 */
	private generateCodeVerifier(): string {
		const array = new Uint8Array(32);
		crypto.getRandomValues(array);
		return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
			"",
		);
	}

	/**
	 * Start the OAuth redirect flow
	 */
	private async startSsoSignIn(
		options: { redirectTo?: string; replace?: boolean } = {},
	): Promise<void> {
		if (typeof window === "undefined") return;

		console.log("[Auth] Starting SSO sign-in flow", {
			redirectTo: options.redirectTo,
			replace: options.replace,
		});

		const redirectTo =
			options.redirectTo || this.config.redirectToAfterSignIn || "/";
		const state = this.generateCodeVerifier();

		localStorage.setItem(AUTH_STATE_KEY, state);
		localStorage.setItem("auth_redirect_to", redirectTo);

		const callbackUrl = `${window.location.origin}/oauth/callback`;
		const authUrl = new URL(`${this.config.authApiUrl}/sso/authorization`);
		authUrl.searchParams.set("provider", "MicrosoftOAuth");
		authUrl.searchParams.set("callback_url", callbackUrl);

		console.log("[Auth] Requesting authorization URL", {
			callbackUrl,
			authApiUrl: this.config.authApiUrl,
		});

		const response = await fetch(authUrl.toString());
		if (!response.ok) {
			console.error("[Auth] Failed to get authorization URL", {
				status: response.status,
				statusText: response.statusText,
			});
			throw new Error("Failed to get authorization URL");
		}

		const data = await response.json();
		console.log("[Auth] Redirecting to authorization URL", {
			authorizationUrl: data.authorization_url,
		});

		if (options.replace) {
			window.location.replace(data.authorization_url);
		} else {
			window.location.href = data.authorization_url;
		}
	}

	/**
	 * Entry point for login button – redirect to custom login page
	 */
	async signIn(
		options: { redirectTo?: string; replace?: boolean } = {},
	): Promise<void> {
		if (typeof window === "undefined") return;

		const redirectTo =
			options.redirectTo ||
			window.location.href ||
			this.config.redirectToAfterSignIn ||
			"/";
		const loginUrl = new URL("/login", window.location.origin);
		if (redirectTo) {
			loginUrl.searchParams.set("redirectTo", redirectTo);
		}

		console.log("[Auth] Redirecting to custom login page", {
			redirectTo,
			loginUrl: loginUrl.toString(),
		});

		if (options.replace) {
			window.location.replace(loginUrl.toString());
		} else {
			window.location.href = loginUrl.toString();
		}
	}

	/**
	 * Initiate Microsoft SSO from custom login page
	 */
	async signInWithMicrosoft(
		options: { redirectTo?: string; replace?: boolean } = {},
	): Promise<void> {
		await this.startSsoSignIn(options);
	}

	/**
	 * Sign up - disabled for this implementation (admin approval required)
	 */
	async signUp(
		options: { redirectTo?: string; replace?: boolean } = {},
	): Promise<void> {
		return this.signIn(options);
	}

	/**
	 * Sign out - clear tokens and redirect
	 */
	async signOut(): Promise<void> {
		console.log("[Auth] Signing out");
		this.clearStoredTokens();

		if (this.tokenRefreshTimer) {
			clearTimeout(this.tokenRefreshTimer);
		}

		// Clear Zudoku's authentication state
		const authState = getAuthState();
		if (authState) {
			authState.setLoggedOut();
			console.log("[Auth] Zudoku auth state cleared");
		}

		if (typeof window !== "undefined") {
			window.location.href = this.config.redirectToAfterSignOut || "/";
		}
	}

	/**
	 * Get current access token, refreshing if necessary
	 */
	async getAccessToken(): Promise<string> {
		const tokens = this.getStoredTokens();

		if (!tokens) {
			console.warn("[Auth] No tokens found, user not authenticated");
			throw new Error("Not authenticated");
		}

		console.log("[Auth] Getting access token", {
			expiresAt: new Date(tokens.expires_at).toISOString(),
			isExpired: this.isTokenExpired(tokens),
		});

		// Refresh token if expired or about to expire
		if (this.isTokenExpired(tokens)) {
			console.log("[Auth] Token expired, refreshing...");
			await this.refreshAccessToken();
			const newTokens = this.getStoredTokens();
			if (!newTokens) {
				console.error("[Auth] Token refresh failed");
				throw new Error("Token refresh failed");
			}
			console.log("[Auth] Token refreshed successfully");
			return newTokens.access_token;
		}

		console.log("[Auth] Using existing access token");
		return tokens.access_token;
	}

	/**
	 * Add Bearer token to request
	 */
	async signRequest(request: Request): Promise<Request> {
		try {
			console.log("[Auth] Signing request", { url: request.url });
			const token = await this.getAccessToken();

			const headers = new Headers(request.headers);
			headers.set("Authorization", `Bearer ${token}`);

			console.log("[Auth] Request signed with Bearer token");
			return new Request(request, { headers });
		} catch (error) {
			// If token retrieval fails, return original request
			console.error("[Auth] Failed to sign request:", error);
			return request;
		}
	}

	/**
	 * Handle OAuth callback on page load
	 * Note: Session restoration is handled in initialize()
	 */
	onPageLoad(): void {
		if (typeof window === "undefined") return;

		console.log("[Auth] onPageLoad called", {
			pathname: window.location.pathname,
			hasCode: window.location.search.includes("code="),
		});

		const url = new URL(window.location.href);
		const code = url.searchParams.get("code");

		// Check if this is an OAuth callback
		if (code && window.location.pathname === "/oauth/callback") {
			console.log("[Auth] Detected OAuth callback, processing code");
			this.handleOAuthCallback(code).catch((error) => {
				console.error("[Auth] OAuth callback failed:", error);
			});
		}
	}

	/**
	 * Handle OAuth callback and exchange code for tokens
	 */
	private async handleOAuthCallback(code: string): Promise<void> {
		console.log("[Auth] Exchanging OAuth code for tokens", {
			codeLength: code.length,
		});

		try {
			const exchangeUrl = `${this.config.authApiUrl}/sso/exchange-code?code=${encodeURIComponent(code)}`;
			console.log("[Auth] Calling exchange-code endpoint", {
				url: exchangeUrl,
			});

			const response = await fetch(exchangeUrl);

			if (!response.ok) {
				const errorText = await response.text();
				console.error("[Auth] Failed to exchange code", {
					status: response.status,
					statusText: response.statusText,
					error: errorText,
				});
				// Try to parse error response as JSON
				let errorCode = "unknown_error";
				let errorDescription = "Failed to exchange code for tokens";

				try {
					const errorData = JSON.parse(errorText);
					errorCode = errorData.error || errorCode;
					errorDescription =
						errorData.message || errorData.error_description || errorDescription;
					console.log("[Auth] Parsed error response", {
						errorCode,
						errorDescription,
					});
				} catch {
					console.warn("[Auth] Could not parse error response as JSON");
				}

				// Redirect to callback page with error parameters
				const callbackUrl = new URL(
					"/oauth/callback",
					window.location.origin,
				);
				callbackUrl.searchParams.set("error", errorCode);
				callbackUrl.searchParams.set("error_description", errorDescription);
				window.location.replace(callbackUrl.toString());
				return;
			}

			const data = await response.json();
			console.log("[Auth] Successfully exchanged code for tokens");
			this.handleTokenResponse(data);

			// Get redirect URL and navigate
			const redirectTo =
				localStorage.getItem("auth_redirect_to") ||
				this.config.redirectToAfterSignIn ||
				"/";
			console.log(
				"[Auth] Authentication complete, redirecting to:",
				redirectTo,
			);

			localStorage.removeItem("auth_redirect_to");
			localStorage.removeItem(AUTH_STATE_KEY);

			window.location.replace(redirectTo);
		} catch (error) {
			console.error("[Auth] OAuth callback error:", error);
			throw error;
		}
	}

	/**
	 * Email+Password signin (optional, if enabled)
	 */
	async signInWithPassword(
		email: string,
		password: string,
		redirectTo?: string,
	): Promise<void> {
		if (!this.config.enablePasswordAuth) {
			throw new Error("Password authentication is not enabled");
		}

		const targetRedirect =
			redirectTo ||
			(typeof window !== "undefined"
				? (new URL(window.location.href).searchParams.get("redirectTo") ??
					undefined)
				: undefined) ||
			this.config.redirectToAfterSignIn ||
			"/";

		if (targetRedirect) {
			localStorage.setItem("auth_redirect_to", targetRedirect);
		}

		const response = await fetch(`${this.config.authApiUrl}/password/signin`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ email, password }),
		});

		if (!response.ok) {
			throw new Error("Sign in failed");
		}

		const data = await response.json();
		this.handleTokenResponse(data);

		if (typeof window !== "undefined") {
			const redirect =
				localStorage.getItem("auth_redirect_to") ||
				this.config.redirectToAfterSignIn ||
				"/";
			localStorage.removeItem("auth_redirect_to");
			window.location.replace(redirect);
		}
	}

	/**
	 * Register OAuth callback route with Zudoku
	 */
	getRoutes() {
		return [
			{
				path: "/oauth/callback",
				element: <OAuthCallback />,
			},
			{
				path: "/signout",
				element: <SignOutRoute />,
			},
		];
	}

	getProfileMenuItems() {
		return [
			{
				label: "API Keys",
				path: "/settings/api-keys",
				category: "middle",
				icon: Key,
			},
			{
				label: "Logout",
				path: "/signout",
				category: "bottom",
				icon: LogOut,
			},
		];
	}
}

/**
 * Factory function to create custom auth provider
 */
export const createCustomAuthProvider = (
	config: CustomAuthConfig,
): AuthenticationPlugin => {
	return new CustomAuthProvider(config);
};

const SignOutRoute = () => {
	const auth = useAuth();

	useEffect(() => {
		void auth.logout();
	}, [auth]);

	return null;
};
